import React from "react";
import { Tabs, Grid, Button } from "antd";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const getAccessibleTabs = (tabs, hasPermission) =>
  tabs.filter((tab) => hasPermission(tab.permission));

const getDefaultTabPath = (tabs, hasPermission) =>
  getAccessibleTabs(tabs, hasPermission)[0]?.path || "/accueil";

export const PermissionTabsRedirect = ({ tabs }) => {
  const { hasPermission } = useAuth();

  return <Navigate to={getDefaultTabPath(tabs, hasPermission)} replace />;
};

const PermissionTabsLayout = ({ tabs, className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const availableTabs = React.useMemo(
    () => getAccessibleTabs(tabs, hasPermission),
    [hasPermission, tabs]
  );

  const activeTab = React.useMemo(
    () =>
      availableTabs.find((tab) => location.pathname.startsWith(tab.path))?.key ||
      availableTabs[0]?.key,
    [availableTabs, location.pathname]
  );

  const handleTabChange = (key) => {
    const targetTab = availableTabs.find((tab) => tab.key === key);

    if (targetTab && targetTab.path !== location.pathname) {
      navigate(targetTab.path);
    }
  };

  if (!availableTabs.length || !activeTab) {
    return <Outlet />;
  }

  const tabsClassName = [
    className,
    className && isMobile ? `${className}--mobile` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const isAuditMobileLayout = isMobile && className === "audit-tabs-layout";

  return (
    <>
      {isAuditMobileLayout && (
        <nav className="audit-mobile-tabbar" aria-label="Navigation audit">
          <div className="audit-mobile-tabbar-grid">
            {availableTabs.map((tab) => (
              <Button
                key={tab.key}
                className="audit-mobile-tabbar-button"
                type={activeTab === tab.key ? "primary" : "default"}
                size="small"
                icon={tab.icon}
                onClick={() => handleTabChange(tab.key)}
                aria-current={activeTab === tab.key ? "page" : undefined}
              >
                {tab.mobileLabel || tab.label}
              </Button>
            ))}
          </div>
        </nav>
      )}
      <Tabs
        className={tabsClassName}
        activeKey={activeTab}
        onChange={handleTabChange}
        size={isMobile ? "middle" : "large"}
        type="card"
        tabBarGutter={isMobile ? 6 : 12}
        tabBarStyle={isAuditMobileLayout ? { display: "none" } : undefined}
        destroyInactiveTabPane
      >
        {availableTabs.map((tab) => (
          <TabPane
            tab={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {tab.icon}
                <span>{isMobile ? tab.mobileLabel || tab.label : tab.label}</span>
              </span>
            }
            key={tab.key}
          >
            <Outlet />
          </TabPane>
        ))}
      </Tabs>
    </>
  );
};

export default PermissionTabsLayout;
